// Builds the live "Orders & Loads" Job Queue rows from real TMS + Directory
// data: un-manifested full-load orders + built loads, merged. Planning-
// intelligence fields (prob / CO₂ / cost / empty km / distance / duration) have
// no backend source yet, so they come through null and render as placeholders.

import type { Address, Party } from '@my/directory-api';
import type {
  CargoDefinition,
  CargoTypeValue,
  Load,
  LoadResourceSummary,
  Order,
  OrderEmissions,
  OrderTask,
  OrderTaskResource,
  ServiceTag,
} from '@my/tms-api';
import { loadSlotLabel, slotLabelFrom } from '@my/tms-api';
import { needsFerry } from '../../bookings/lib/crossing';
import { CARGO_HANDLING_TASK_TYPES, cargoHandlingPhaseFor } from '../../orders/lib/cargoPick';
import { cargoInfoRows, type CargoInfoRow } from '../../orders/lib/cargoInfo';
import { fmtDuration } from '../../orders/lib/journeyPlan';
import { nextPointTask } from '../../orders/lib/orderPoints';

export type PlanMovementKind = 'fullLoad' | 'load';

export type GroupMode = 'Flat' | 'Driver' | 'Direction';
/** Direction grouping bucket + the chip label/flow shown in the Direction column. */
export type DirectionFlow = 'out' | 'in' | 'local';

/** Schedule-based on-time read (planned window vs today) — not a travel-time ETA. */
export type ProbValue = 'On time' | 'At risk' | 'Tight';

export const DIRECTION_SECTIONS: { flow: DirectionFlow; label: string }[] = [
  { flow: 'out', label: 'Outbound' },
  { flow: 'in', label: 'Inbound' },
  { flow: 'local', label: 'Local' },
];

/**
 * The Job Queue search: every word must appear somewhere on the row — job
 * number, customer, driver, vehicle, trailer, cargo, the Info column (BIC,
 * Tank ID, goods), origin / destination or status. Case-insensitive.
 */
export function matchesSearch(m: PlanMovement, search: string): boolean {
  const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const haystack = [
    m.jobNo,
    m.customer,
    m.driver,
    m.vehicle,
    m.trailer,
    m.origin,
    m.dest,
    m.status,
    ...m.cargo,
    ...m.infoRows.flatMap((r) => [r.value, r.title]),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return words.every((w) => haystack.includes(w));
}

export interface PlanMovement {
  id: string;
  jobNo: string;
  /** When the order / load was created — the "Newest" sort. */
  createdUtc: string;
  kind: PlanMovementKind;
  /** Underlying order id — set for full-load orders, null for built loads. */
  orderId: string | null;
  /**
   * Underlying load id — set for built loads, null for orders. Assignment writes
   * need it: a load spans several orders, so it has its own scoped endpoint.
   */
  loadId: string | null;
  /**
   * The crew currently on this movement, for merging a new assignment into.
   * Populated for loads (projected onto the LoadDto BE-side); empty for orders,
   * whose assignments are fetched on demand for the selected row only.
   */
  resources: LoadResourceSummary[];
  /**
   * The tasks this movement covers, in execution order. An order's own tasks; for
   * a load, the distinct tasks its lines reach — which can span several orders.
   * Used by the allocation panel to show per-stop crew.
   */
  tasks: OrderTask[];
  customer: string;
  /** Display badges — cargo type label plus service-tag names. */
  cargo: string[];
  /**
   * Cargo *type* labels only ("Full Load", "Pallet"…), kept separate from
   * `cargo` so the cargo filter can't accidentally match a service tag. A load
   * spanning several orders can carry more than one.
   */
  cargoTypes: string[];
  /**
   * The identifying detail of what is on this job — BIC codes, Tank IDs or the
   * goods, plus the target temperature on a temperature order. Same rows the
   * orders list's Info column renders, from the same function, so the planner
   * and the operator cannot be told different things about the same job.
   */
  infoRows: CargoInfoRow[];
  origin: string;
  dest: string;
  ferry: boolean;
  window: { label: string; kind: 'fixed' | 'flex' };
  driver: string | null;
  vehicle: string | null;
  trailer: string | null;
  /** Real order/load status (e.g. Draft, Confirmed, InProgress, Planned, Dispatched). */
  status: string;
  /** Earliest planned day (yyyy-MM-dd), for the "At risk" (overdue) filter. */
  plannedYmd: string | null;
  /** Derived on-time read from the planned window vs today. Null when unplanned. */
  prob: ProbValue | null;
  /**
   * The order's stored laden CO2 estimate (Directory works it out, TMS keeps
   * it). Null for a load of several orders — their journeys are not the
   * truck's — and until the estimate arrives.
   */
  co2Kg: number | null;
  /**
   * Laden driving distance and time from the same stored estimate: road legs
   * only (truck-routed by Azure), the ferry crossing left out. Null when CO2
   * is null.
   */
  distKm: number | null;
  durLabel: string | null;
  /**
   * Part of the journey is a straight-line estimate (a leg Azure couldn't
   * route, a stop with no location) or it is out of date: show "~" on the
   * CO2 and the distance.
   */
  journeyApprox: boolean;
  // ── Net-new planning signals — no backend source yet, always null. ──
  costEur: number | null;
  emptyKm: number | null;
}

/** The cockpit's journey figures off an order's stored estimate. */
function journeyOf(
  e: OrderEmissions | undefined,
): Pick<PlanMovement, 'co2Kg' | 'distKm' | 'durLabel' | 'journeyApprox'> {
  if (!e) return { co2Kg: null, distKm: null, durLabel: null, journeyApprox: false };
  const road = e.legs.filter((l) => l.kind === 'Road');
  const minutes = road.reduce((sum, l) => sum + (l.minutes ?? 0), 0);
  return {
    co2Kg: e.co2Kg,
    distKm: e.ladenKm,
    durLabel: minutes > 0 ? fmtDuration(minutes) : null,
    journeyApprox: !e.complete || e.stale || road.some((l) => l.minutes == null),
  };
}

/** Planned-day vs today → a coarse on-time read. */
function probFor(plannedYmd: string | null, status: string, todayYmd: string): ProbValue | null {
  if (status === 'Completed') return 'On time';
  if (!plannedYmd) return null;
  if (plannedYmd < todayYmd) return 'At risk'; // overdue
  if (plannedYmd === todayYmd) return 'Tight'; // due today
  return 'On time'; // future
}

const PROB_ACCENT: Record<ProbValue, string> = {
  'On time': 'text-emerald-600',
  'At risk': 'text-red-600',
  Tight: 'text-amber-600',
};

export function probAccent(p: ProbValue): string {
  return PROB_ACCENT[p];
}

const CARGO_TYPE_LABEL: Partial<Record<CargoTypeValue, string>> = {
  FullLoad: 'Full Load',
  PartLoad: 'Part Load',
};

function windowLabel(task: OrderTask): string {
  if (task.timeMode === 'AM') return 'AM';
  if (task.timeMode === 'PM') return 'PM';
  if (task.timeMode === 'Exact' && task.plannedTime) return task.plannedTime.slice(0, 5);
  return 'Any';
}

/** "2026-06-05" → "05/06/26". */
function fmtDmy(iso: string): string {
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}/${m}/${y.slice(2)}` : iso;
}

/** An order's cargo *type* label ("Full Load", "Pallet"…), or null if undefined. */
function orderCargoType(cargoDef: CargoDefinition | undefined): string | null {
  if (!cargoDef) return null;
  return CARGO_TYPE_LABEL[cargoDef.cargoType] ?? cargoDef.cargoType;
}

/**
 * The Info rows for a movement — one order's, or every order on a load,
 * de-duplicated (three orders at -18°C is one temperature, not three).
 *
 * <p>
 * `pending` is false because a movement only EXISTS once its cargo-handling
 * tasks are in hand — a row with none is skipped by the builder — so there is
 * never a moment here where the cargo half has to be withheld.
 * </p>
 *
 * <p>
 * A load's rows are built per ORDER, not per load line: a load may carry only
 * part of an order's cargo, so the goods summary can read wider than what is
 * actually on this truck. Order granularity is what the `cargo` badges beside
 * it already use, and narrowing it would need the line → item → description
 * walk for a gain nobody has asked for.
 * </p>
 */
function movementInfoRows(
  movementOrders: Order[],
  cargoDefsById: Map<string, CargoDefinition>,
  tasksByOrderId: Map<string, OrderTask[]>,
): CargoInfoRow[] {
  const seen = new Set<string>();
  const out: CargoInfoRow[] = [];
  for (const order of movementOrders) {
    const rows = cargoInfoRows(
      order,
      cargoDefsById.get(order.cargoDefinitionId),
      tasksByOrderId.get(order.id) ?? [],
      false,
    );
    for (const row of rows) {
      // Stringified rather than joined on a separator: a caption and a value
      // can both be arbitrary text, and this is a dedupe key, not an identity.
      const key = JSON.stringify(row);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(row);
    }
  }
  return out;
}

/** Cargo badges for an order: its cargo type + resolved service-tag names. */
function orderCargo(
  order: Order,
  cargoDef: CargoDefinition | undefined,
  serviceTagsById: Map<string, ServiceTag>,
): string[] {
  const out: string[] = [];
  if (cargoDef) out.push(CARGO_TYPE_LABEL[cargoDef.cargoType] ?? cargoDef.cargoType);
  for (const id of order.serviceTagIds) {
    const name = serviceTagsById.get(id)?.name;
    if (name) out.push(name);
  }
  return out;
}

export function buildMovements(args: {
  orders: Order[];
  tasksByOrderId: Map<string, OrderTask[]>;
  loads: Load[];
  customersById: Map<string, Party>;
  addressById: Map<string, Address>;
  serviceTagsById: Map<string, ServiceTag>;
  cargoDefsById: Map<string, CargoDefinition>;
  /** Depot site id → country ISO (for a load's origin). */
  siteCountryById: Map<string, string>;
  /**
   * Every assignment in the organisation, flat. Grouped to orders here via the
   * tasks already loaded — the DTO is task-scoped, and the task→order mapping is
   * right there in `tasksByOrderId`.
   */
  assignments: OrderTaskResource[];
  /** Today as yyyy-MM-dd, for the derived on-time read. */
  todayYmd: string;
  /** Stored CO2 estimates by order id. */
  emissionsByOrderId?: Map<string, OrderEmissions>;
}): PlanMovement[] {
  const {
    orders,
    tasksByOrderId,
    loads,
    customersById,
    addressById,
    serviceTagsById,
    cargoDefsById,
    siteCountryById,
    assignments,
    todayYmd,
  } = args;

  const ordersById = new Map(orders.map((o) => [o.id, o]));

  // Task id → owning order, then assignments grouped per order. Loads don't need
  // this: their crew is projected onto the LoadDto server-side.
  const orderIdByTaskId = new Map<string, string>();
  for (const [orderId, tasks] of tasksByOrderId) {
    for (const t of tasks) orderIdByTaskId.set(t.id, orderId);
  }
  const assignmentsByOrderId = new Map<string, OrderTaskResource[]>();
  for (const a of assignments) {
    const oid = orderIdByTaskId.get(a.orderTaskId);
    if (!oid) continue;
    const list = assignmentsByOrderId.get(oid);
    if (list) list.push(a);
    else assignmentsByOrderId.set(oid, [a]);
  }
  const country = (addressId: string | null | undefined) =>
    (addressId ? addressById.get(addressId)?.countryIsoCode : undefined) || '—';

  // Cargo-item id → owning order (so a load's lines resolve back to customers) and
  // → owning task (so a load can list the stops its lines actually touch).
  const orderIdByItemId = new Map<string, string>();
  const taskByItemId = new Map<string, OrderTask>();
  for (const [orderId, tasks] of tasksByOrderId) {
    for (const t of tasks) {
      for (const it of t.cargoItems ?? []) {
        orderIdByItemId.set(it.id, orderId);
        taskByItemId.set(it.id, t);
      }
    }
  }

  // Orders already represented on a load drop out of the "to plan" order rows.
  const orderIdsOnLoad = new Set<string>();
  for (const load of loads) {
    for (const line of load.lines) {
      const oid = orderIdByItemId.get(line.orderTaskCargoItemId);
      if (oid) orderIdsOnLoad.add(oid);
    }
  }

  const rows: PlanMovement[] = [];

  // ── Un-manifested full-load orders ──
  for (const order of orders) {
    if (order.status === 'Cancelled') continue;
    if (orderIdsOnLoad.has(order.id)) continue;
    const cargoDef = cargoDefsById.get(order.cargoDefinitionId);
    const tasks = (tasksByOrderId.get(order.id) ?? [])
      .slice()
      .sort((a, b) => a.sequence - b.sequence);
    const handling = tasks.filter((t) => CARGO_HANDLING_TASK_TYPES.has(t.type));
    // A movement needs at least one cargo-handling stop — there's nothing to plan
    // for an order with nothing to collect or deliver. This also drops orders with
    // no tasks at all, and orders carrying only process events (Stop / Instruction
    // / Wash / …), since cargo items only ever hang off handling tasks.
    if (handling.length === 0) continue;
    const collTask = handling.find((t) => cargoHandlingPhaseFor(t.type) === 'load');
    const delTask = handling.find((t) => cargoHandlingPhaseFor(t.type) === 'delivery');
    const origin = country(collTask?.addressId);
    const dest = country(delTask?.addressId);
    const plannedYmd =
      handling
        .map((t) => t.plannedDate)
        .filter((d): d is string => !!d)
        .sort()[0] ?? null;
    const orderAssignments = assignmentsByOrderId.get(order.id) ?? [];

    rows.push({
      id: `order:${order.id}`,
      jobNo: order.orderNumber,
      createdUtc: order.createdUtc,
      kind: 'fullLoad',
      orderId: order.id,
      loadId: null,
      resources: orderAssignments,
      tasks,
      customer: customersById.get(order.customerPartyId)?.name ?? '—',
      cargo: orderCargo(order, cargoDef, serviceTagsById),
      cargoTypes: [orderCargoType(cargoDef)].filter((t): t is string => t !== null),
      infoRows: movementInfoRows([order], cargoDefsById, tasksByOrderId),
      origin,
      dest,
      ferry: needsFerry(origin === '—' ? null : origin, dest === '—' ? null : dest) === true,
      window: collTask
        ? {
            label: collTask.plannedDate
              ? `${fmtDmy(collTask.plannedDate)} ${windowLabel(collTask)}`
              : windowLabel(collTask),
            kind: collTask.timeMode === 'Exact' ? 'fixed' : 'flex',
          }
        : { label: '—', kind: 'flex' },
      driver: slotLabelFrom(orderAssignments, 'Driver'),
      vehicle: slotLabelFrom(orderAssignments, 'Vehicle'),
      trailer: slotLabelFrom(orderAssignments, 'Trailer'),
      status: order.status,
      plannedYmd,
      prob: probFor(plannedYmd, order.status, todayYmd),
      ...journeyOf(args.emissionsByOrderId?.get(order.id)),
      costEur: null,
      emptyKm: null,
    });
  }

  // ── Built loads ──
  for (const load of loads) {
    if (load.status === 'Cancelled') continue;
    const loadOrderIds = [
      ...new Set(
        load.lines
          .map((l) => orderIdByItemId.get(l.orderTaskCargoItemId))
          .filter((o): o is string => !!o),
      ),
    ];
    const loadOrders = loadOrderIds.map((id) => ordersById.get(id)).filter((o): o is Order => !!o);

    const customerNames = [
      ...new Set(
        loadOrders
          .map((o) => customersById.get(o.customerPartyId)?.name)
          .filter((n): n is string => !!n),
      ),
    ];
    const customer =
      customerNames.length === 0
        ? '—'
        : customerNames.length === 1
          ? customerNames[0]!
          : `${customerNames[0]} +${customerNames.length - 1}`;

    const cargo = [
      ...new Set(
        loadOrders.flatMap((o) =>
          orderCargo(o, cargoDefsById.get(o.cargoDefinitionId), serviceTagsById),
        ),
      ),
    ].slice(0, 3);

    // Every distinct cargo type across the load's orders.
    const cargoTypes = [
      ...new Set(
        loadOrders
          .map((o) => orderCargoType(cargoDefsById.get(o.cargoDefinitionId)))
          .filter((t): t is string => t !== null),
      ),
    ];

    const origin = siteCountryById.get(load.depotSiteId) ?? '—';
    const dest = country(load.furthestPointAddressId);

    /*
     * Distinct stops the load's lines reach, in the LOAD's running order.
     *
     * Ordered by the earliest line sequence touching each stop, NOT by
     * `OrderTask.Sequence`: that counts from 1 within its own order, so on a load
     * mixing several orders it would interleave them arbitrarily. The load's line
     * sequence is the only ordering that spans the whole run.
     */
    const earliestLineSeq = new Map<string, number>();
    for (const line of load.lines) {
      const task = taskByItemId.get(line.orderTaskCargoItemId);
      if (!task) continue;
      const seen = earliestLineSeq.get(task.id);
      if (seen === undefined || line.sequence < seen) earliestLineSeq.set(task.id, line.sequence);
    }
    const loadTasks = [
      ...new Map(
        load.lines
          .map((line) => taskByItemId.get(line.orderTaskCargoItemId))
          .filter((t): t is OrderTask => t !== undefined)
          .map((t) => [t.id, t] as const),
      ).values(),
    ].sort(
      (a, b) =>
        (earliestLineSeq.get(a.id) ?? 0) - (earliestLineSeq.get(b.id) ?? 0) ||
        a.sequence - b.sequence,
    );

    rows.push({
      id: `load:${load.id}`,
      jobNo: load.manifestNumber,
      createdUtc: load.createdUtc,
      kind: 'load',
      orderId: null,
      loadId: load.id,
      resources: load.resources,
      tasks: loadTasks,
      customer,
      cargo,
      cargoTypes,
      infoRows: movementInfoRows(loadOrders, cargoDefsById, tasksByOrderId),
      origin,
      dest,
      ferry: needsFerry(origin === '—' ? null : origin, dest === '—' ? null : dest) === true,
      window: { label: load.plannedDate ? fmtDmy(load.plannedDate) : '—', kind: 'flex' },
      // Crew comes from the tasks' assignments, projected onto the load BE-side.
      // The driver column was permanently empty before this — Load had no driver
      // column at all.
      driver: loadSlotLabel(load, 'Driver'),
      vehicle: loadSlotLabel(load, 'Vehicle'),
      trailer: loadSlotLabel(load, 'Trailer'),
      status: load.status,
      plannedYmd: load.plannedDate ?? null,
      prob: probFor(load.plannedDate ?? null, load.status, todayYmd),
      // One order's journey is the load's; several orders' are not the truck's.
      ...journeyOf(
        loadOrderIds.length === 1 ? args.emissionsByOrderId?.get(loadOrderIds[0]!) : undefined,
      ),
      costEur: null,
      emptyKm: null,
    });
  }

  return rows;
}

// ── Sorting (the Sort dropdown, as on the Orders screen) ────────────────────

export type PlanSortKey = 'jobNo' | 'customer' | 'startPoint' | 'nextPoint';
export type PlanSortDir = 'asc' | 'desc';

/** The Sort dropdown, in the order it lists them. `null` is the default, newest first. */
export const PLAN_SORT_OPTIONS: { key: PlanSortKey | null; label: string }[] = [
  { key: null, label: 'Newest' },
  { key: 'jobNo', label: 'Job number' },
  { key: 'customer', label: 'Customer' },
  { key: 'startPoint', label: 'Start point date/time' },
  { key: 'nextPoint', label: 'Next point date/time' },
];

export const isPlanSortKey = (v: unknown): v is PlanSortKey =>
  PLAN_SORT_OPTIONS.some((o) => o.key !== null && o.key === v);

/** A stop's planned moment as sortable text; AM / PM sit at the start of their half-day. */
function plannedKey(t: OrderTask | undefined): string | null {
  if (!t?.plannedDate) return null;
  const time = t.plannedTime?.slice(0, 5) ?? (t.timeMode === 'PM' ? '12:00' : '00:00');
  return `${t.plannedDate} ${time}`;
}

/**
 * The queue in the chosen order. Newest is always newest first (no
 * direction). Anything without the value sorted on — no customer, no planned
 * date — goes last either way; ties keep the newest first.
 */
export function sortMovements(
  movements: PlanMovement[],
  key: PlanSortKey | null,
  dir: PlanSortDir,
): PlanMovement[] {
  const newestFirst = (a: PlanMovement, b: PlanMovement) => b.createdUtc.localeCompare(a.createdUtc);
  if (key === null) return [...movements].sort(newestFirst);

  const valueOf = (m: PlanMovement): string | null => {
    switch (key) {
      case 'jobNo':
        return m.jobNo;
      case 'customer':
        return m.customer && m.customer !== '—' ? m.customer : null;
      case 'startPoint':
        return plannedKey(m.tasks[0]);
      case 'nextPoint':
        // Once every stop is done, its last one — so finished jobs still sort.
        return plannedKey(nextPointTask(m.tasks) ?? m.tasks[m.tasks.length - 1]);
    }
  };
  const sign = dir === 'asc' ? 1 : -1;
  return [...movements]
    .map((m) => ({ m, v: valueOf(m) }))
    .sort((a, b) => {
      if (a.v === null || b.v === null) {
        if (a.v !== b.v) return a.v === null ? 1 : -1;
        return newestFirst(a.m, b.m);
      }
      return (
        sign * a.v.localeCompare(b.v, undefined, { numeric: true, sensitivity: 'base' }) ||
        newestFirst(a.m, b.m)
      );
    })
    .map((x) => x.m);
}

// ── What Plan shows at all ──────────────────────────────────────────────────

/**
 * Plan is for work that is ready to be done. Nothing in Draft is — a Draft
 * order lives on the Orders screen until it's opened, a Draft load on Manifest
 * until it's planned — and anything Cancelled never will be. Completed work
 * only shows when asked for.
 */
export function isOnPlan(m: PlanMovement, showCompleted: boolean): boolean {
  if (m.status === 'Draft' || m.status === 'Cancelled') return false;
  if (m.status === 'Completed') return showCompleted;
  return true;
}

// ── Job Queue filter chips (real signals) ───────────────────────────────────

export const PLAN_QUEUE_FILTERS = [
  'All',
  'Standing',
  'Unassigned',
  'At Risk',
  'In Progress',
  'Planned',
] as const;
export type PlanQueueFilter = (typeof PLAN_QUEUE_FILTERS)[number];

export function matchesQueueFilter(m: PlanMovement, f: PlanQueueFilter, todayYmd: string): boolean {
  switch (f) {
    case 'All':
      return true;
    // Waiting to start: an Open order (Drafts aren't on Plan at all).
    case 'Standing':
      return m.status === 'Open';
    case 'Unassigned':
      return m.vehicle == null;
    case 'At Risk':
      return m.plannedYmd != null && m.plannedYmd < todayYmd && m.status !== 'Completed';
    case 'In Progress':
      return m.status === 'InProgress' || m.status === 'Dispatched';
    case 'Planned':
      return m.status === 'Planned' || m.status === 'Confirmed';
  }
}

/** Status → badge classes for the real order/load statuses. */
const STATUS_ACCENT: Record<string, string> = {
  Draft: 'bg-slate-100 text-slate-600',
  Confirmed: 'bg-blue-100 text-blue-700',
  Planned: 'bg-violet-100 text-violet-700',
  InProgress: 'bg-indigo-100 text-indigo-700',
  Dispatched: 'bg-sky-100 text-sky-700',
  Completed: 'bg-emerald-100 text-emerald-700',
  Cancelled: 'bg-rose-100 text-rose-700',
};

export function statusAccent(status: string): string {
  return STATUS_ACCENT[status] ?? 'bg-slate-100 text-slate-600';
}

/** Human label for a raw status value (spaces out the camel-case ones). */
export function statusLabel(status: string): string {
  return status === 'InProgress' ? 'In Progress' : status;
}

// ── Cargo + Direction filters ────────────────────────────────────────────────

/** Sentinel for "no filter". Kept a plain string so it can live in the store. */
export const FILTER_ALL = 'All';

/**
 * Country flow for a movement, e.g. "IE → GB". Null when either end is unknown,
 * which is common while an order still has no addresses on its tasks.
 */
export function directionOf(m: PlanMovement): string | null {
  if (m.origin === '—' || m.dest === '—') return null;
  return `${m.origin} → ${m.dest}`;
}

/**
 * Direction options taken from what's actually on screen — the set of country
 * pairs present in the current queue. Domestic flows (IE → IE) sort first, then
 * alphabetically, so the common case leads.
 */
export function directionOptions(movements: PlanMovement[]): string[] {
  const seen = new Set<string>();
  for (const m of movements) {
    const d = directionOf(m);
    if (d) seen.add(d);
  }
  return [...seen].sort((a, b) => {
    const aLocal = a.split(' → ')[0] === a.split(' → ')[1];
    const bLocal = b.split(' → ')[0] === b.split(' → ')[1];
    if (aLocal !== bLocal) return aLocal ? -1 : 1;
    return a.localeCompare(b);
  });
}

/** Cargo-type options from the organisation's own cargo definitions. */
export function cargoTypeOptions(cargoDefs: CargoDefinition[]): string[] {
  const seen = new Set<string>();
  for (const d of cargoDefs) seen.add(CARGO_TYPE_LABEL[d.cargoType] ?? d.cargoType);
  return [...seen].sort((a, b) => a.localeCompare(b));
}

export function matchesCargoFilter(m: PlanMovement, cargoType: string): boolean {
  return cargoType === FILTER_ALL || m.cargoTypes.includes(cargoType);
}

export function matchesDirectionFilter(m: PlanMovement, direction: string): boolean {
  return direction === FILTER_ALL || directionOf(m) === direction;
}

// ── Grouping ─────────────────────────────────────────────────────────────────

/**
 * Which way a job flows **relative to the selected depot's country**:
 *   - `out`   leaves that country  (IE depot: IE → GB)
 *   - `in`    arrives into it      (IE depot: GB → IE)
 *   - `local` stays inside it      (IE depot: IE → IE)
 *
 * Null when the depot country is unknown, or the job has no origin/destination
 * yet — those rows can't be placed and land in an "Unknown" bucket instead of
 * being silently dropped. Note a GB → FR job at an IE depot is also null: it
 * touches neither end, so calling it inbound or outbound would be a lie.
 */
export function directionFlowOf(
  m: PlanMovement,
  depotCountry: string | null,
): DirectionFlow | null {
  if (!depotCountry) return null;
  const from = m.origin === '—' ? null : m.origin;
  const to = m.dest === '—' ? null : m.dest;
  if (!from || !to) return null;

  const fromHome = from === depotCountry;
  const toHome = to === depotCountry;
  if (fromHome && toHome) return 'local';
  if (fromHome) return 'out';
  if (toHome) return 'in';
  return null;
}

/** One rendered section of the queue. */
export interface MovementGroup {
  key: string;
  label: string;
  rows: PlanMovement[];
}

/**
 * Split the queue into display sections for the chosen Group mode. `Flat`
 * returns a single unlabelled section so the table renders exactly as before.
 * Empty sections are dropped; row order within a section is preserved.
 */
export function groupMovements(
  movements: PlanMovement[],
  group: GroupMode,
  depotCountry: string | null,
): MovementGroup[] {
  if (group === 'Flat') return [{ key: 'flat', label: '', rows: movements }];

  if (group === 'Direction') {
    const buckets = new Map<DirectionFlow | 'unknown', PlanMovement[]>();
    for (const m of movements) {
      const flow = directionFlowOf(m, depotCountry) ?? 'unknown';
      (buckets.get(flow) ?? buckets.set(flow, []).get(flow)!).push(m);
    }
    const out: MovementGroup[] = [];
    for (const { flow, label } of DIRECTION_SECTIONS) {
      const rows = buckets.get(flow);
      if (rows?.length) out.push({ key: flow, label, rows });
    }
    const unknown = buckets.get('unknown');
    if (unknown?.length) {
      out.push({
        key: 'unknown',
        // Says why rather than just "Unknown": almost always a missing address.
        label: depotCountry ? 'No country flow' : 'No depot country',
        rows: unknown,
      });
    }
    return out;
  }

  // Driver — one section per assigned driver, unassigned last.
  const byDriver = new Map<string, PlanMovement[]>();
  const unassigned: PlanMovement[] = [];
  for (const m of movements) {
    if (!m.driver) {
      unassigned.push(m);
      continue;
    }
    (byDriver.get(m.driver) ?? byDriver.set(m.driver, []).get(m.driver)!).push(m);
  }
  const out = [...byDriver.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([driver, rows]) => ({ key: `driver:${driver}`, label: driver, rows }));
  if (unassigned.length) out.push({ key: 'unassigned', label: 'Unassigned', rows: unassigned });
  return out;
}
